import { QueryClient } from '@tanstack/react-query';

import { isApiError } from '@/lib/api/errors';

/** Retries a request only when retrying could plausibly change the answer. */
const shouldRetry = (failureCount: number, error: unknown): boolean => {
  if (failureCount >= 2) {
    return false;
  }

  if (!isApiError(error)) {
    return false;
  }

  /*
   * A dropped connection or a timeout is worth another go. A 4xx is the server
   * stating a fact about the request — retrying it just delays the error the
   * user needs to see. 5xx gets one more attempt in case it was transient.
   */
  if (error.kind === 'network' || error.kind === 'timeout') {
    return true;
  }

  return error.status !== null && error.status >= 500;
};

/**
 * Build the application's `QueryClient`.
 *
 * A factory rather than a module-level singleton so tests can hand each case a
 * clean cache, and so the instance is created inside the module that owns the
 * router context rather than as an import side effect.
 */
export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        /*
         * Atlas is a review tool people leave parked on a second monitor. A
         * refetch storm every time the window regains focus would restart
         * in-flight reads while a reviewer is mid-comment, so focus is not a
         * refetch trigger; explicit invalidation after a mutation is.
         */
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        /*
         * Long enough that navigating between a list and a detail and back does
         * not refetch, short enough that a colleague's upload shows up on the
         * next deliberate visit.
         */
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
      },
      mutations: {
        /*
         * Never automatic. Every mutation here creates, edits or deletes
         * something, and a silent replay of a create is worse than an error the
         * user can act on.
         */
        retry: false,
      },
    },
  });
